import { useState, useEffect, useCallback, useRef } from 'react';
import Arrow from '../../assets/Arrow.svg';

const Gallery = ({ images, title }) => {
	const [index, setIndex] = useState(-1);
	const closeButtonRef = useRef(null);

	const handleClose = useCallback(() => {
		setIndex(-1);
	}, []);

	const handleKeyDown = useCallback(
		(e) => {
			switch (e.key) {
				case 'Escape':
					handleClose();
					break;
				case 'ArrowLeft':
					if (index > 0) {
						setIndex(index - 1);
					}
					break;
				case 'ArrowRight':
					if (index < images.length - 1) {
						setIndex(index + 1);
					}
					break;
			}
		},
		[handleClose, index, images.length]
	);

	useEffect(() => {
		if (index >= 0) {
			// Add event listener for keyboard navigation
			window.addEventListener('keydown', handleKeyDown);
			// Focus the close button when lightbox opens
			closeButtonRef.current?.focus();
			// Prevent body scroll
			document.body.style.overflow = 'hidden';
		} else {
			// Remove event listener when lightbox closes
			window.removeEventListener('keydown', handleKeyDown);
			// Restore body scroll
			document.body.style.overflow = '';
		}

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
			document.body.style.overflow = '';
		};
	}, [index, handleKeyDown]);

	return (
		<div className="mb-12">
			{title && <h2 className="font-trade-compressed uppercase text-[1.875rem] font-bold tracking-widest mb-6 leading-[1.2]" dangerouslySetInnerHTML={{ __html: title }} />}
			<div className="grid gap-6 md:grid-cols-3">
				{images.map((image, idx) => (
					<button key={idx} className="w-full aspect-[360/269] relative overflow-hidden bg-gray-100 cursor-pointer" onClick={() => setIndex(idx)}>
						<img src={image.src} alt={image.caption || ''} className="object-cover w-full h-full" />
					</button>
				))}
			</div>

			{index >= 0 && (
				<div className="overflow-hidden fixed inset-0 z-50 transition-opacity duration-150 ease-in-out" role="dialog" aria-modal="true" aria-label="Image lightbox">
					<div
						className="grid h-full grid-rows-[auto_1fr_auto] bg-white opacity-0 animate-fadeIn"
						role="document"
						style={{
							animation: 'fadeIn 300ms ease-in-out forwards',
						}}
					>
						{/* Header */}
						<header className="container flex justify-end items-center py-6">
							<button ref={closeButtonRef} className="flex justify-center items-center gap-[8px] focus:outline-none" onClick={handleClose} aria-label="Close lightbox" title="Press Escape to close">
								<span>Close</span>
								<span className="h-[38px] w-[38px] flex items-center justify-center bg-[#FCB94C]">
									<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="white">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
									</svg>
								</span>
							</button>
						</header>

						{/* Main Content */}
						<main className="overflow-hidden flex-1 min-h-0" role="region" aria-label={`Image ${index + 1} of ${images.length}`}>
							<div className="container flex relative justify-center items-center p-4 h-full max-w-container-lg">
								<img src={images[index].src} alt={images[index].alt || `Image ${index + 1}`} className="object-contain w-auto max-h-full" style={{ maxWidth: 'calc(100vw - 2rem)' }} role="img" tabIndex="0" />
							</div>
						</main>

						{/* Footer */}
						<footer className="container flex justify-between items-center py-6">
							{images[index]?.caption && (
								<div className="">
									<span className="font-bold">Kelli Connell</span>,{' '}
									<span
										className="italic"
										dangerouslySetInnerHTML={{
											__html: images[index].caption,
										}}
									/>
									{images[index].year && <span className="">{`, ${images[index].year}`}</span>}
									<span className="">, archival inkjet print, 30&#8243; x 40&#8243;</span>
								</div>
							)}

							<nav className="flex gap-3" aria-label="Image navigation">
								{index > 0 && (
									<button className="inline-flex items-center bg-[#F58F87] h-[38px] w-[38px] justify-center" onClick={() => setIndex(index - 1)} aria-label="Previous image" title="Previous image (Left arrow key)">
										<img src={Arrow} className="scale-x-[-1] max-w-[17px]" />
										<span className="sr-only">Previous</span>
									</button>
								)}
								{index < images.length - 1 && (
									<button className="inline-flex items-center bg-[#F58F87] h-[38px] w-[38px] justify-center" onClick={() => setIndex(index + 1)} aria-label="Next image" title="Next image (Right arrow key)">
										<span className="sr-only">Next</span>
										<img src={Arrow} className="max-w-[17px]" />
									</button>
								)}
							</nav>
						</footer>
					</div>
				</div>
			)}
		</div>
	);
};

// Add keyframes for fade-in animation
const fadeInKeyframes = `
@keyframes fadeIn {
	from { opacity: 0; }
	to { opacity: 1; }
}
`;

// Add style tag to head
if (typeof document !== 'undefined') {
	const style = document.createElement('style');
	style.textContent = fadeInKeyframes;
	document.head.appendChild(style);
}

export default Gallery;
